export type TelStore =
  | 'spans'
  | 'logs'
  | 'errorGroups'
  | 'metrics'
  | 'alerts'
  | 'remoteTraces'

const DB_VERSION = 3

/** `request.transaction` is spec-guaranteed non-null inside `upgradeneeded`. */
function _assertTransaction(tx: IDBTransaction | null): IDBTransaction {
  if (!tx) throw new Error('IndexedDB upgrade transaction missing')
  return tx
}

const connections = new Map<string, Promise<IDBDatabase>>()

export function openTelDb(name = 'tel'): Promise<IDBDatabase> {
  const cached = connections.get(name)
  if (cached) return cached
  const opened = new Promise<IDBDatabase>((resolve, reject) => {
    const request = indexedDB.open(name, DB_VERSION)
    request.addEventListener('error', () => {
      reject(request.error ?? new Error('IndexedDB open failed'))
    })
    request.addEventListener('success', () => {
      const db = request.result
      // A schema bump in another tab blocks until every open connection
      // closes; close ours so that tab's `upgradeneeded` never wedges.
      db.addEventListener('versionchange', () => {
        db.close()
        connections.delete(name)
      })
      resolve(db)
    })
    request.addEventListener('upgradeneeded', () => {
      const db = request.result
      const tx = request.transaction
      if (!db.objectStoreNames.contains('spans')) {
        const spans = db.createObjectStore('spans', { keyPath: 'id' })
        for (const key of ['traceId', 'ingestedAt', 'target', 'status'])
          spans.createIndex(key, key)
      }
      if (!db.objectStoreNames.contains('logs')) {
        const logs = db.createObjectStore('logs', { keyPath: 'id' })
        for (const key of ['traceId', 'ingestedAt']) logs.createIndex(key, key)
      }
      if (db.objectStoreNames.contains('errorGroups')) {
        // `request.transaction` is always non-null inside `upgradeneeded`.
        const errorGroups = _assertTransaction(tx).objectStore('errorGroups')
        if (!errorGroups.indexNames.contains('lastSeen'))
          errorGroups.createIndex('lastSeen', 'lastSeen')
      } else {
        const errorGroups = db.createObjectStore('errorGroups', {
          keyPath: 'id',
        })
        errorGroups.createIndex('lastSeen', 'lastSeen')
      }
      if (!db.objectStoreNames.contains('metrics')) {
        const metrics = db.createObjectStore('metrics', { keyPath: 'id' })
        for (const key of ['ingestedAt', 'name']) metrics.createIndex(key, key)
      }
      if (!db.objectStoreNames.contains('alerts'))
        db.createObjectStore('alerts', { autoIncrement: true })
      const remoteTraces = db.objectStoreNames.contains('remoteTraces')
        ? _assertTransaction(tx).objectStore('remoteTraces')
        : db.createObjectStore('remoteTraces', { keyPath: 'traceId' })
      if (!remoteTraces.indexNames.contains('at'))
        remoteTraces.createIndex('at', 'at')
    })
  })
  connections.set(name, opened)
  // A failed open (no IndexedDB yet, blocked, quota) must not poison every later open of this database.
  opened.catch(() => { if (connections.get(name) === opened) connections.delete(name) })
  return opened
}

interface StoreConfig {
  keyOf?: (value: unknown) => IDBValidKey
  mergeIngestedAt?: boolean
  /** Index to prune by (ascending, oldest first). Omitted = primary key. */
  pruneIndex?: string
}
const storeConfigs: Record<TelStore, StoreConfig> = {
  spans: { keyOf: (v) => (v as { id: string }).id, mergeIngestedAt: true },
  logs: { keyOf: (v) => (v as { id: string }).id, mergeIngestedAt: true },
  errorGroups: {
    keyOf: (v) => (v as { id: string }).id,
    pruneIndex: 'lastSeen',
  },
  metrics: {
    keyOf: (v) => (v as { id: string }).id,
    mergeIngestedAt: true,
    pruneIndex: 'ingestedAt',
  },
  alerts: {},
  remoteTraces: {
    keyOf: (v) => (v as { traceId: string }).traceId,
    pruneIndex: 'at',
  },
}

interface Resolver {
  resolve: () => void
  reject: (error: unknown) => void
}
interface WriteQueue {
  pendingByKey: Map<IDBValidKey, unknown>
  pendingKeyless: unknown[]
  resolvers: Resolver[]
  running: Promise<void> | null
  /** Retention cap for this store, applied inside every commit. */
  cap?: number
}
const queues = new Map<string, WriteQueue>()

function _queue(dbName: string, storeName: TelStore): WriteQueue {
  const qKey = `${dbName}:${storeName}`
  let queue = queues.get(qKey)
  if (!queue) {
    queue = {
      pendingByKey: new Map(),
      pendingKeyless: [],
      resolvers: [],
      running: null,
    }
    queues.set(qKey, queue)
  }
  return queue
}

/**
 * Coalescing writer: at most one write transaction in flight per
 * `dbName:storeName`. Writes arriving while a transaction is in flight join
 * the next one, latest value wins per key. No timers. The returned promise
 * resolves when the transaction holding this call's data commits, and
 * rejects (without wedging later writes) if that transaction fails.
 * `cap`, when given, prunes the oldest rows beyond it inside the same
 * commit transaction, after the puts land — never a separate transaction.
 */
export function putMany(
  dbName: string,
  storeName: TelStore,
  values: unknown[],
  cap?: number,
): Promise<void> {
  const queue = _queue(dbName, storeName)
  if (cap !== undefined) queue.cap = cap
  if (values.length === 0 && cap === undefined) return Promise.resolve()
  const config = storeConfigs[storeName]
  for (const value of values) {
    if (config.keyOf) queue.pendingByKey.set(config.keyOf(value), value)
    else queue.pendingKeyless.push(value)
  }
  const promise = new Promise<void>((resolve, reject) => {
    queue.resolvers.push({ resolve, reject })
  })
  queue.running ??= _drain(dbName, storeName, queue)
  return promise
}

async function _drain(
  dbName: string,
  storeName: TelStore,
  queue: WriteQueue,
): Promise<void> {
  while (
    queue.pendingByKey.size > 0 ||
    queue.pendingKeyless.length > 0 ||
    queue.cap !== undefined
  ) {
    const values = [...queue.pendingByKey.values(), ...queue.pendingKeyless]
    queue.pendingByKey.clear()
    queue.pendingKeyless = []
    const cap = queue.cap
    queue.cap = undefined
    const batch = queue.resolvers
    queue.resolvers = []
    if (values.length === 0 && cap === undefined) break
    try {
      await _commit(dbName, storeName, values, cap)
      notifyTelDb(dbName)
      for (const resolver of batch) resolver.resolve()
    } catch (error) {
      // The failed batch's callers reject; the queue keeps draining so
      // later, valid writes still commit instead of wedging forever.
      for (const resolver of batch) resolver.reject(error)
    }
  }
  // Synchronous with the loop's exit (no extra microtask hop via .finally),
  // so a write queued by a `putMany` continuation scheduled from this same
  // drain's last commit never observes a stale truthy `running`.
  queue.running = null
}

async function _commit(
  dbName: string,
  storeName: TelStore,
  values: unknown[],
  cap: number | undefined,
): Promise<void> {
  const db = await openTelDb(dbName)
  const config = storeConfigs[storeName]
  return new Promise((resolve, reject) => {
    const tx = db.transaction(storeName, 'readwrite')
    const store = tx.objectStore(storeName)
    let pendingMerges = 0
    const maybePrune = () => {
      if (pendingMerges > 0 || cap === undefined) return
      const countRequest = store.count()
      countRequest.addEventListener('success', () => {
        const excess = countRequest.result - cap
        if (excess <= 0) return
        const source = config.pruneIndex
          ? store.index(config.pruneIndex)
          : store
        const keysRequest = source.getAllKeys(undefined, excess)
        keysRequest.addEventListener('success', () => {
          for (const key of keysRequest.result) store.delete(key)
        })
        keysRequest.addEventListener('error', () => {
          reject(keysRequest.error ?? new Error('IndexedDB keys failed'))
        })
      })
      countRequest.addEventListener('error', () => {
        reject(countRequest.error ?? new Error('IndexedDB count failed'))
      })
    }
    const mergeAndPut = (value: unknown, keyOf: (value: unknown) => IDBValidKey) => {
      pendingMerges++
      const getRequest = store.get(keyOf(value))
      getRequest.addEventListener('success', () => {
        const existing = getRequest.result as { ingestedAt?: number } | undefined
        const ingestedAt =
          existing?.ingestedAt ??
          (value as { ingestedAt?: number }).ingestedAt ??
          Date.now()
        store.put({ ...(value as object), ingestedAt })
        pendingMerges--
        maybePrune()
      })
    }
    for (const value of values) {
      if (config.mergeIngestedAt && config.keyOf) mergeAndPut(value, config.keyOf)
      else store.put(value)
    }
    maybePrune()
    tx.addEventListener('complete', () => {
      resolve()
    })
    tx.addEventListener('error', () => {
      reject(tx.error ?? new Error('IndexedDB transaction failed'))
    })
    tx.addEventListener('abort', () => {
      reject(tx.error ?? new Error('IndexedDB transaction aborted'))
    })
  })
}

/** Awaits every in-flight + queued write for this dbName across all stores. */
export async function flushTelDb(dbName: string): Promise<void> {
  const running: Promise<void>[] = []
  for (const [key, queue] of queues) {
    if (key.startsWith(`${dbName}:`) && queue.running)
      running.push(queue.running)
  }
  if (running.length === 0) return
  await Promise.all(running)
  await flushTelDb(dbName)
}

export async function getOne<T>(
  dbName: string,
  storeName: TelStore,
  key: IDBValidKey,
): Promise<T | undefined> {
  const db = await openTelDb(dbName)
  return request(
    db.transaction(storeName).objectStore(storeName).get(key) as IDBRequest<
      T | undefined
    >,
  )
}

export async function getAll<T>(
  dbName: string,
  storeName: TelStore,
): Promise<T[]> {
  const db = await openTelDb(dbName)
  return request(
    db.transaction(storeName).objectStore(storeName).getAll() as IDBRequest<
      T[]
    >,
  )
}

export async function getAllFromIndex<T>(
  dbName: string,
  storeName: TelStore,
  indexName: string,
  range?: IDBKeyRange,
): Promise<T[]> {
  const db = await openTelDb(dbName)
  return request(
    db
      .transaction(storeName)
      .objectStore(storeName)
      .index(indexName)
      .getAll(range) as IDBRequest<T[]>,
  )
}

function request<T>(value: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    value.addEventListener('success', () => {
      resolve(value.result)
    })
    value.addEventListener('error', () => {
      reject(value.error ?? new Error('IndexedDB request failed'))
    })
  })
}

export function lowerBound(lower: number): IDBKeyRange {
  return IDBKeyRange.lowerBound(lower)
}

// --- Change notification -------------------------------------------------

const listeners = new Map<string, Set<() => void>>()
// One BroadcastChannel per db, shared for both sending and receiving.
// BroadcastChannel never delivers a message back to the sender, so the
// direct `listeners` loop covers this context and the channel covers only
// other tabs/workers — no double notification, and nothing to dedupe.
const channels = new Map<string, BroadcastChannel>()

function _channel(dbName: string): BroadcastChannel | undefined {
  if (typeof BroadcastChannel === 'undefined') return undefined
  let channel = channels.get(dbName)
  if (!channel) {
    channel = new BroadcastChannel(`tel:${dbName}`)
    channel.addEventListener('message', () => {
      for (const fn of listeners.get(dbName) ?? []) fn()
    })
    channels.set(dbName, channel)
  }
  return channel
}

/** Subscribes to every committed write for `dbName`: same-context listeners
 * plus a `BroadcastChannel` for other tabs/workers when defined (not on RN). */
export function subscribeTelDb(dbName: string, listener: () => void): () => void {
  let set = listeners.get(dbName)
  if (!set) {
    set = new Set()
    listeners.set(dbName, set)
  }
  const listenerSet = set
  listenerSet.add(listener)
  _channel(dbName)
  return () => {
    listenerSet.delete(listener)
    if (listenerSet.size === 0) {
      channels.get(dbName)?.close()
      channels.delete(dbName)
    }
  }
}

function notifyTelDb(dbName: string): void {
  for (const fn of listeners.get(dbName) ?? []) fn()
  // oxlint-disable-next-line unicorn/require-post-message-target-origin -- BroadcastChannel is origin-scoped and accepts no targetOrigin
  _channel(dbName)?.postMessage(1)
}
